import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Calendar, Users, ArrowRight } from 'lucide-react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import SEO from '../components/SEO';

interface Tournament {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  order: number;
  isActive: boolean;
}

// HTML'den düz metin çıkaran yardımcı fonksiyon
const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const Turnuvalar: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);

  useEffect(() => {
    const loadTournaments = async () => {
      try {
        // Index gerektirmemesi için sadece orderBy kullan, filtreleme client-side yapılacak
        const tournamentQuery = query(
          collection(db, 'tournaments'),
          orderBy('order', 'asc')
        );
        const tournamentSnapshot = await getDocs(tournamentQuery);
        const tournamentData = tournamentSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          } as Tournament))
          .filter(tournament => tournament.isActive === true);
        setTournaments(tournamentData);
      } catch (error) {
        console.error('Turnuvalar yüklenirken hata:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTournaments();
  }, []);

  return (
    <>
      <SEO
        title="Turnuvalar | Heyecan Verici Yarışmalar"
        description="Heyecan verici turnuvalara katılın ve büyük ödüller kazanın."
        url="/etkinlikler/turnuvalar"
      />
      <div className="min-h-screen bg-dark-900 pt-24 pb-12 relative overflow-hidden">
        {/* Background - Anasayfa ile aynı */}
        <div className="absolute inset-0 bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 via-transparent to-primary-600/10"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/25">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Turnuvalar
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Heyecan verici turnuvalara katılın ve büyük ödüller kazanın
            </p>
          </motion.div>

          {/* Loading State */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-dark-800/50 rounded-2xl overflow-hidden animate-pulse">
                  <div className="h-48 bg-dark-700/50" />
                  <div className="p-6 space-y-3">
                    <div className="h-6 bg-dark-700/50 rounded w-3/4" />
                    <div className="h-4 bg-dark-700/50 rounded w-full" />
                    <div className="h-4 bg-dark-700/50 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : tournaments.length === 0 ? (
            /* Empty State */
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-dark-800/50 backdrop-blur-sm rounded-2xl p-12 border border-dark-700 text-center max-w-lg mx-auto"
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-dark-700/50 flex items-center justify-center">
                <Calendar className="w-10 h-10 text-gray-500" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-4">Yakında</h2>
              <p className="text-gray-400">
                Turnuva takvimi yakında burada olacak. Takipte kalın!
              </p>
            </motion.div>
          ) : (
            /* Tournaments Grid */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tournaments.map((tournament, index) => (
                <motion.div
                  key={tournament.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group bg-dark-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-dark-700 hover:border-amber-500/50 transition-all duration-300 cursor-pointer flex flex-col"
                  onClick={() => setSelectedTournament(tournament)}
                >
                  {/* Image */}
                  {tournament.imageUrl && (
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={tournament.imageUrl}
                        alt={tournament.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/20 to-transparent" />

                      {/* Order Badge */}
                      <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-amber-500/90 backdrop-blur-sm text-white text-sm font-semibold">
                        #{tournament.order}
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-amber-400 transition-colors line-clamp-2">
                      {tournament.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed line-clamp-3 flex-1">
                      {stripHtml(tournament.description)}
                    </p>

                    {/* Action */}
                    <div className="mt-4 pt-4 border-t border-dark-700/50 flex items-center justify-between">
                      <span className="text-sm text-gray-500 flex items-center">
                        <Users className="w-4 h-4 mr-1" />
                        Aktif
                      </span>
                      <span className="text-amber-400 text-sm font-medium flex items-center group-hover:translate-x-1 transition-transform">
                        Detaylar
                        <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedTournament && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedTournament(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-dark-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-dark-700"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedTournament.imageUrl && (
              <div className="relative h-64">
                <img
                  src={selectedTournament.imageUrl}
                  alt={selectedTournament.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-800 to-transparent" />
              </div>
            )}
            <div className="p-6 -mt-16 relative">
              <div className="flex items-start justify-between mb-4">
                <h2 className="text-2xl font-bold text-white">{selectedTournament.title}</h2>
                <button
                  onClick={() => setSelectedTournament(null)}
                  className="p-2 rounded-lg bg-dark-700 hover:bg-dark-600 text-gray-400 hover:text-white transition-colors"
                >
                  ✕
                </button>
              </div>
              <div
                className="prose prose-invert max-w-none text-gray-300 leading-relaxed overflow-y-auto max-h-[50vh]"
                dangerouslySetInnerHTML={{ __html: selectedTournament.description }}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </>
  );
};

export default Turnuvalar;
